
'use client';

import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  type Firestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { ReactNode, createContext, useContext, useEffect, useState, useMemo } from 'react';
import { logPerf } from '@/lib/perf';
import { firebaseConfig } from './config';
import { FirebaseErrorListener } from '@/components/FirebaseErrorListener';
import type { User } from 'firebase/auth';

type FirebaseServices = {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
};

type FirebaseContextType = FirebaseServices & {
  user: User | null;
  loading: boolean;
};

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export function FirebaseClientProvider({ children }: { children: ReactNode }) {
  // Initialize Firebase services synchronously but memoized
  // This is safe because Firebase SDK is designed for immediate initialization
  const services = useMemo<FirebaseServices>(() => {
    try {
      console.log('Initializing Firebase with config:', firebaseConfig);
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
      const auth = getAuth(app);
      let firestore: Firestore;
      if (typeof window === 'undefined') {
        firestore = getFirestore(app);
      } else {
        try {
          // Multi-tab, not single-tab: with a single-tab manager only one tab can hold the
          // IndexedDB lease, and the tabs that lose it can stall — listeners never fire and
          // the page sits on a loading screen until you close the other tabs.
          firestore = initializeFirestore(app, {
            localCache: persistentLocalCache({
              tabManager: persistentMultipleTabManager(),
            }),
          });
        } catch {
          try {
            // Already initialized for this app — reuse it.
            firestore = getFirestore(app);
          } catch {
            // Persistence is unavailable (private browsing, blocked storage, no IndexedDB).
            // An in-memory cache still works; it just doesn't survive a reload.
            console.warn('Firestore persistence unavailable; falling back to in-memory cache.');
            firestore = initializeFirestore(app, { localCache: memoryLocalCache() });
          }
        }
      }
      const storage = getStorage(app);
      console.log('Firebase initialized successfully');
      
      return { app, auth, firestore, storage };
    } catch (error) {
      console.error('Firebase initialization error:', error);
      throw error;
    }
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Setting up auth state listener');
    const unsubscribe = onAuthStateChanged(services.auth, (user) => {
      console.log('Auth state changed:', user ? 'logged in' : 'logged out');
      setUser(user);
      setLoading(false);
      logPerf('auth_state_ready', { signedIn: !!user });
    }, (error) => {
      console.error('Auth state error:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [services.auth]);

  const contextValue = useMemo(() => ({
    ...services,
    user,
    loading
  }), [services, user, loading]);

  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
      <FirebaseErrorListener />
    </FirebaseContext.Provider>
  );
};

export function useFirebase() {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseClientProvider');
  }
  return context;
};

export const useAuth = () => useFirebase().auth;
export const useFirestore = () => useFirebase().firestore;
export const useStorage = () => useFirebase().storage;
export const useUser = () => {
    const { user, loading } = useFirebase();
    return { user, loading };
}
